import { useEffect, useMemo, useRef, useState } from 'react';
import { throttle } from 'lodash-es';

import { SECTIONS } from '../components/AiTabNavigation';
import scrollPage from '../../../utils/scrollPage';
import { getScrollableContainer } from '../../../utils/helpers';

const PAGE_HEADER_HEIGHT = 72;
const STICKY_HEADER_HEIGHT = 78;

export default function useAiDashboardNav(stickySentinelRef) {
  const scrollableAreaRef = useRef(getScrollableContainer());
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    let observer;

    if (stickySentinelRef?.current) {
      const isWindow = scrollableAreaRef.current === window;

      observer = new IntersectionObserver(
        ([entry]) => {
          setIsSticky(!entry.isIntersecting);
        },
        {
          root: isWindow ? null : scrollableAreaRef.current,
          threshold: 0,
        }
      );
      observer.observe(stickySentinelRef.current);
    }

    return () => {
      observer?.disconnect();
    };
  }, [stickySentinelRef]);

  useEffect(() => {
    const isWindow = scrollableAreaRef.current === window;
    const sectionElements = SECTIONS.map((s) => document.getElementById(s.id)).filter(Boolean);

    const handleScroll = throttle(() => {
      const target = isWindow ? window : scrollableAreaRef.current;
      const scrollTop = isWindow ? window.pageYOffset : target.scrollTop;
      const scrollHeight = isWindow ? document.documentElement.scrollHeight : target.scrollHeight;
      const clientHeight = isWindow ? window.innerHeight : target.clientHeight;

      if (scrollHeight - scrollTop - clientHeight <= 50) {
        setActiveSection(SECTIONS[SECTIONS.length - 1].id);
        return;
      }

      const offset = STICKY_HEADER_HEIGHT + PAGE_HEADER_HEIGHT;

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        if (sectionElements[i].getBoundingClientRect().top <= offset) {
          setActiveSection(SECTIONS[i].id);
          return;
        }
      }
      setActiveSection(SECTIONS[0].id);
    }, 100);

    const target = isWindow ? window : scrollableAreaRef.current;
    target.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      target.removeEventListener('scroll', handleScroll);
      handleScroll.cancel();
    };
  }, []);

  const active = useMemo(
    () => SECTIONS.find((s) => s.id === activeSection) || SECTIONS[0],
    [activeSection]
  );

  return { isSticky, active, showTopApps: active.id !== 'market-overview' };
}

export const scrollToSection = (sectionId) => {
  const el = document.getElementById(sectionId);
  if (!el) return;

  const scrollableArea = getScrollableContainer();
  const isWindow = scrollableArea === window;
  const containerOffset = isWindow ? 0 : scrollableArea.getBoundingClientRect().top;
  const destination =
    el.getBoundingClientRect().top +
    (isWindow ? window.pageYOffset : scrollableArea.scrollTop) -
    containerOffset -
    STICKY_HEADER_HEIGHT;

  scrollPage({ destination, scrollableArea });
};
